import baustein from "baustein";
import _extend from "lodash/extend";
import _isElement from "lodash/isElement";
import events from "web/script/dom/events";
import globalEvents from "./global-events";

var defaultOptions = {
    zIndex: 1000,
    parent: document.body,
};
var matte;
var shown;
var currentInstance;

/**
 * Toggles no-scroll class
 * @function toggleBodyScroll
 */
function toggleBodyScroll() {
    document.body.classList.toggle("no-scroll");
}

/**
 * Initialize the matte and parses the options
 * @options {
 *   blocked: should the matte be closable?
 * }
 * @constructor Matte
 */
function Matte(options) {
    this.options = _extend({}, defaultOptions, options);

    if (!matte) {
        matte = document.createElement("div");
        matte.classList.add("matte");
    }

    if (_isElement(this.options.parent)) {
        this.options.parent.appendChild(matte);
    }

    this.container = matte;

    if (currentInstance) {
        currentInstance.close();
    }

    // eslint-disable-next-line @typescript-eslint/no-this-alias
    currentInstance = this;

    if (this.options.white) {
        this.container.classList.add("white");
    } else {
        this.container.classList.remove("white");
    }

    this._onBodyEvent = this._onBodyEvent.bind(this);
}

Matte.prototype = {
    /**
     * On matte click check if it's the matte div and if so close the thing
     */
    _onBodyEvent: function (event) {
        var fullscreenGallery = baustein.fromElement(document.querySelector(".fullscreen-gallery"));

        // the fullscreen gallery component will catch the 'esc' keyup
        // and close itself (before closing the entire overlay)
        if (fullscreenGallery && fullscreenGallery.isAttached()) {
            return;
        }

        if (event.keyCode === 27 && event.target.tagName !== "INPUT") {
            // ESC key but not if focused on an input
            this.close();
        } else if (event.type === "click" && matte === event.target) {
            // click event on the matte
            this.close();
        }
    },

    /**
     * Hides the matte
     * @returns {Promise} A Promise that will resolve when the Matte is hidden.
     */
    hide: function () {
        return new Promise(function (resolve) {
            if (!shown) {
                resolve();
                return;
            }

            shown = false;

            events
                .onTransitionEnd(matte, (el) => (el.style.opacity = "0"), null, "opacity")
                .then(() => {
                    toggleBodyScroll();
                    resolve();
                });
        });
    },

    /**
     * Shows the matte
     * @returns {Promise} A promise that will resolve when the matte is shown.
     */
    show: function () {
        return new Promise(
            function (resolve) {
                if (shown) {
                    return Promise.resolve();
                }

                var zIndex = this.options.zIndex;

                shown = true;

                function trigger() {
                    matte.style.zIndex = zIndex;

                    // it is important that the following two changes happen inside sequential
                    // rAF's otherwise the transitionEnd event for the 'opacity' won't always fire
                    requestAnimationFrame(function () {
                        matte.style.opacity = "1";
                    });
                }

                events.onTransitionEnd(matte, trigger, null, "opacity").then(() => {
                    resolve();
                    toggleBodyScroll();
                });
            }.bind(this)
        );
    },

    /**
     * Sets onClick handler if non-blocking
     * @returns {Promise} A promise that will resolve when the matt is opened.
     */
    open: function () {
        var promise = this.show();

        if (!this.options.blocked) {
            document.body.addEventListener("keyup", this._onBodyEvent, false);
            document.body.addEventListener("click", this._onBodyEvent, false);
        }

        return promise;
    },

    /**
     * Closes the matte and resets events.
     * @param {boolean} [force] If force is true then the closing of the matte cannot be aborted.
     * @returns {Promise}
     */
    close: function (force) {
        var aborted = false;

        if (!force) {
            globalEvents.trigger("matte.beforeclose", {
                abort: function () {
                    aborted = true;
                },
            });
        }

        if (aborted) {
            return Promise.resolve();
        }

        document.body.removeEventListener("keyup", this._onBodyEvent, false);
        document.body.removeEventListener("click", this._onBodyEvent, false);
        currentInstance = null;

        return this.hide().then(function () {
            globalEvents.trigger("matte.close");
            if (matte.parentElement) {
                matte.parentElement.removeChild(matte);
            }
        });
    },

    /**
     * Show the loading class on the matte
     */
    showLoading: function () {
        matte.classList.add("loading");
    },

    /**
     * Remove the loading class on the matte
     */
    removeLoading: function () {
        matte.classList.remove("loading");
    },
};

export default Matte;
